import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Dialog, DialogTitle } from '@mui/material';

import { updateClaimQueue } from '../../../../store/participantService';
import { getNameFromId } from '../../../../utils/directusFunctions';
import { clearKeys } from '../../../../utils/objectUtils';
import { hasRole, PRAP, QUALITY, SUPERUSER } from '../../../../utils/userRoles';
import Button from '../../../formElements/Button';
import SingleSelect from '../../../formElements/SingleSelect';
import TextAreaField from '../../../formElements/TextAreaField';

import { initialErrorState, NOTE_CHAR_LIMIT, validate } from './validateStatusEditor';

import form from '../../../../commonStyles/formStyles.module.css';
import modal from '../../../../commonStyles/modal.module.css';

const acceptedRoles = [PRAP, QUALITY, SUPERUSER];

const StatusEditor = ({
    open,
    onStatusChangeEditor,
    statusRow,
    claimEventStatuses,
    claimEventStatusReasons,
    claimQueue,
    claimEvent
}) => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        statusId: '',
        reasonId: '',
        notes: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [isOpen, setIsOpen] = React.useState(false);
    const [claimEventStatusesTrimmed, setClaimEventStatusesTrimmed] = useState([]);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [errors, setErrors] = useState(initialErrorState);
    const [keys, setKeys] = useState({
        statusId: '0',
        reasonId: '1'
    });

    // STORE STATE
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);

    // USE EFFECTS
    useEffect(() => {
        if (Object.keys(claimQueue)?.length < 1) return;

        const eventStatuses = claimEvent.claimTypeStatuses.find(
            (el) => el.statusId === statusRow.claimQueueStatusId
        );
        if (eventStatuses) {
            const eventToStatusIds = eventStatuses.claimTypeToStatusIds;
            setClaimEventStatusesTrimmed(
                claimEventStatuses.filter((el) => eventToStatusIds.some((entry) => entry === el.id))
            );
        }
    }, [claimQueue]);

    useEffect(() => {
        setIsOpen(open);
    }, [open]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === `Claim queue has been updated`) {
            setIsUpdateButtonDisabled(false);
            clearData();
        }
    }, [successMessage]);

    // HELPER FNS

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };
    const clearData = () => {
        setNewEntry(initialState);
        setErrors(initialErrorState);
        setKeys(clearKeys(keys));
        onStatusChangeEditor(false);
        setErrors({});
        isUpdateButtonDisabled && setIsUpdateButtonDisabled(false);
    };

    // EVENT HANDLERS
    const onInputUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onStatusUpdate = (value) => {
        clearError('statusId');
        'reasonId' in errors && clearError('reasonId');
        setNewEntry((prev) => ({ ...prev, statusId: value, reasonId: '' }));
    };

    const onCancel = () => clearData();

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, claimEventStatuses);
        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;
        const payload = { ...newEntry, id: claimQueue.id };
        dispatch(updateClaimQueue(payload));
    };

    // RENDER
    return (
        <Dialog
            open={isOpen}
            onClose={onCancel}
            aria-labelledby="form-dialog-title"
            fullWidth={true}>
            <div className={modal.modalHeader}>
                <DialogTitle id="form-dialog-title">Update Status</DialogTitle>
            </div>
            <div className={modal.modalContent}>
                <form
                    onSubmit={onSubmit}
                    className={form.formWrapper}
                    data-testid="update_status_form_start">
                    <SingleSelect
                        id={'statusId'}
                        key={keys.statusId}
                        label={'Status'}
                        placeholder="Select status..."
                        mandatory={true}
                        disabled={!hasRole(acceptedRoles, roles)}
                        menuItems={claimEventStatusesTrimmed || []}
                        selectedId={newEntry.statusId || ''}
                        selected={
                            claimEventStatusesTrimmed.find((el) => el.id === newEntry?.statusId) ||
                            {}
                        }
                        error={errors.statusId}
                        onChange={(chosenId) => onStatusUpdate(chosenId)}
                    />
                    {getNameFromId(claimEventStatuses, newEntry.statusId).toLowerCase() ===
                        'fail' && (
                        <SingleSelect
                            id={'reasonId'}
                            key={keys.reasonId}
                            label={'Reason'}
                            placeholder="Select reason..."
                            mandatory={!hasRole(acceptedRoles, roles)}
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={claimEventStatusReasons || []}
                            selectedId={newEntry.reasonId || ''}
                            selected={
                                claimEventStatusReasons.find(
                                    (el) => el.id === newEntry?.reasonId
                                ) || {}
                            }
                            error={errors.reasonId}
                            onChange={(chosenId) => onInputUpdate('reasonId', chosenId)}
                        />
                    )}
                    <TextAreaField
                        id="notes"
                        label="Notes"
                        placeholder={'Reversion notes'}
                        disabled={!hasRole(acceptedRoles, roles)}
                        mandatory={true}
                        maxLength={NOTE_CHAR_LIMIT}
                        rows={5}
                        value={newEntry.notes || ''}
                        count={`${newEntry.notes.length}/${NOTE_CHAR_LIMIT}`}
                        error={errors.notes}
                        onChange={(e) => onInputUpdate('notes', e.target.value)}
                    />

                    <div className={modal.actionButtons}>
                        <Button
                            id="statusEditorUpdateButton"
                            disabled={!hasRole(acceptedRoles, roles) || isUpdateButtonDisabled}
                            content="Update"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                        <Button
                            id="statusEditorCancelButton"
                            type="button"
                            content="Cancel"
                            onClick={onCancel}
                        />
                    </div>
                </form>
            </div>
        </Dialog>
    );
};

StatusEditor.propTypes = {
    open: PropTypes.bool.isRequired,
    onStatusChangeEditor: PropTypes.func,
    statusRow: PropTypes.object,
    claimEventStatuses: PropTypes.array,
    claimEventStatusReasons: PropTypes.array,
    claimQueue: PropTypes.object,
    claimEvent: PropTypes.object
};

export default StatusEditor;
